import React, { useState, useRef, useEffect } from 'react';
import { FiCamera, FiX, FiAlertCircle, FiType } from 'react-icons/fi';
import { BsQrCodeScan } from 'react-icons/bs';
import { paymentService } from '../services';
import type { QRPaymentData } from '../types';

interface QRCodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (data: QRPaymentData) => void;
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({ isOpen, onClose, onScan }) => {
  const [mode, setMode] = useState<'camera' | 'manual'>('manual');
  const [manualInput, setManualInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup camera on unmount or close
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Handle camera mode
  useEffect(() => {
    if (mode === 'camera' && isOpen) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [mode, isOpen]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Não foi possível aceder à câmara. Verifique as permissões.');
      setMode('manual');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const handleManualSubmit = () => {
    setError(null);

    if (!manualInput.trim()) {
      setError('Por favor, insira os dados do QR Code');
      return;
    }

    const parsed = paymentService.parseQRCode(manualInput.trim());

    if (!parsed) {
      setError('Formato de QR Code inválido. Use: ENTIDADE|REFERENCIA|MONTANTE ou apenas a referência.');
      return;
    }

    onScan(parsed);
    setManualInput('');
    onClose();
  };

  const handleClose = () => {
    stopCamera();
    setManualInput('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-pagali-gray-200">
          <div className="flex items-center gap-2">
            <BsQrCodeScan className="text-xl text-pagali-primary" />
            <h3 className="text-lg font-semibold text-pagali-gray-800">Pagamento QR Code</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-500 transition-colors"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex border-b border-pagali-gray-200">
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors
              ${mode === 'manual'
                ? 'text-pagali-primary border-b-2 border-pagali-primary'
                : 'text-pagali-gray-500 hover:text-pagali-gray-700'}`}
          >
            <FiType />
            Inserir Manualmente
          </button>
          <button
            onClick={() => setMode('camera')}
            className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors
              ${mode === 'camera'
                ? 'text-pagali-primary border-b-2 border-pagali-primary'
                : 'text-pagali-gray-500 hover:text-pagali-gray-700'}`}
          >
            <FiCamera />
            Usar Câmara
          </button>
        </div>

        <div className="p-6">
          {mode === 'camera' ? (
            <div className="space-y-4">
              {cameraError ? (
                <div className="p-4 bg-red-50 text-red-600 rounded-xl text-center">
                  <FiAlertCircle className="mx-auto text-2xl mb-2" />
                  <p className="text-sm">{cameraError}</p>
                </div>
              ) : (
                <>
                  <div className="relative aspect-square bg-black rounded-xl overflow-hidden">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {/* Scan overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-48 h-48 border-2 border-white/50 rounded-xl relative">
                        <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-pagali-primary rounded-tl-lg" />
                        <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-pagali-primary rounded-tr-lg" />
                        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-pagali-primary rounded-bl-lg" />
                        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-pagali-primary rounded-br-lg" />
                      </div>
                    </div>
                  </div>
                  <p className="text-center text-sm text-pagali-gray-500">
                    Aponte a câmara para o código QR
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
                  Dados do QR Code
                </label>
                <textarea
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  placeholder="Cole aqui os dados do QR Code ou insira no formato:&#10;ENTIDADE|REFERENCIA|MONTANTE&#10;ou apenas a referência"
                  rows={4}
                  className={`w-full px-4 py-3 border rounded-xl resize-none
                    focus:outline-none focus:ring-2 focus:ring-pagali-primary/20 focus:border-pagali-primary
                    ${error ? 'border-pagali-error' : 'border-pagali-gray-200'}`}
                />
                {error && (
                  <p className="text-pagali-error text-xs mt-1 flex items-center gap-1">
                    <FiAlertCircle /> {error}
                  </p>
                )}
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Formatos aceites:</strong>
                </p>
                <ul className="text-xs text-blue-600 mt-1 space-y-1">
                  <li>&bull; ENTIDADE|REFERENCIA|MONTANTE</li>
                  <li>&bull; Referência (6-15 dígitos)</li>
                  <li>&bull; JSON estruturado</li>
                </ul>
              </div>

              <button
                onClick={handleManualSubmit}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
              >
                <BsQrCodeScan />
                Processar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanner;
